import { ReactNode, useCallback } from 'react';
import { isEqual } from 'lodash';
import { Field, FieldRenderProps, RenderableProps } from 'react-final-form';

import { RetailerNetworkLocationFilter, RetailerNetworkLocationDataFragment } from '@occc/graphql';
import {
  AutocompleteProps,
  FormInputProps,
  LazyAutocomplete,
  showErrorOnBlurOrDirty,
} from '@occc-app/design-system';

import { useGetRetailerNetworkLocationsLazyQuery } from '../../data-access/queries';

type RetailerNetworkLocationSelectContainerProps = Pick<AutocompleteProps, 'onFocus' | 'onBlur'> & {
  children?: ReactNode;
  value: RetailerNetworkLocationDataFragment[];
  onChange: (values: RetailerNetworkLocationDataFragment[]) => void;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  errorText?: string;
  inputProps?: FormInputProps;
  retailerNetworkUuidEquals: string;
  openCommerceUserUuidEquals?: string;
};

type RetailerNetworkLocationSelectFieldProps = {
  name: string;
  label: string;
} & Pick<
  RetailerNetworkLocationSelectContainerProps,
  'inputProps' | 'retailerNetworkUuidEquals' | 'openCommerceUserUuidEquals'
>;

const makeFilter = ({
  retailerNetworkUuidEquals,
  openCommerceUserUuidEquals,
  search,
}: RetailerNetworkLocationFilter): RetailerNetworkLocationFilter => ({
  retailerNetworkUuidEquals,
  openCommerceUserUuidEquals,
  search: search || null,
});

const mapQueryResponseToRawArray = (
  response: ReturnType<typeof useGetRetailerNetworkLocationsLazyQuery>['response'],
) => {
  return response.data?.map((item) => item.location) || [];
};

const mapRawResponseItemToAutocompleteOption = (location: RetailerNetworkLocationDataFragment) => {
  const { uuid, name, siteSystemConfiguration } = location;
  return {
    value: uuid,
    label: siteSystemConfiguration?.[0]?.identifier || name || uuid,
    _raw: location,
  };
};

const LocationSelect: React.FC<RetailerNetworkLocationSelectContainerProps> = ({
  retailerNetworkUuidEquals,
  openCommerceUserUuidEquals,
  ...restProps
}) => (
  <LazyAutocomplete<
    RetailerNetworkLocationDataFragment,
    ReturnType<typeof useGetRetailerNetworkLocationsLazyQuery>['response']
  >
    label='Locations'
    {...restProps}
    useLazyQuery={useGetRetailerNetworkLocationsLazyQuery}
    makeQueryFilter={(search: string) =>
      makeFilter({ retailerNetworkUuidEquals, search, openCommerceUserUuidEquals })
    }
    mapQueryResponseToRawArray={mapQueryResponseToRawArray}
    mapRawResponseItemToAutocompleteOption={mapRawResponseItemToAutocompleteOption}
  />
);

export const RetailerNetworkLocationMultipleSelectField = (
  props: RetailerNetworkLocationSelectFieldProps,
) => {
  const renderInput = useCallback<
    NonNullable<RenderableProps<FieldRenderProps<RetailerNetworkLocationDataFragment[]>>['render']>
  >(
    ({ input, meta }) => (
      <LocationSelect
        {...props}
        value={input.value || []}
        onFocus={input.onFocus}
        onBlur={input.onBlur}
        onChange={input.onChange}
        errorText={showErrorOnBlurOrDirty({ meta }) ? meta.error : undefined}
      />
    ),
    [props],
  );

  return <Field name={props.name} isEqual={isEqual} render={renderInput} />;
};
